import { Checkbox, cn, Stack } from 'component-library';
import React from 'react';

import { CoPilotConfig, Industry, RoleDescription } from '@/api/rest/resources/types/organization';

import { useOnboardingFormContext } from './OnboardingForm';

type FormCardProps = {
  title?: string;
  description?: string;
  isMultipleChoice?: boolean;
  formField: keyof CoPilotConfig;
  className?: string;
  disabled: boolean;
};

type FormCardFieldProps = { formValue: RoleDescription | Industry } & FormCardProps;

export const FormCardField = ({
  title,
  description,
  formField,
  formValue,
  className,
  disabled,
}: FormCardFieldProps) => {
  const { setValue, watch } = useOnboardingFormContext();
  const selectedFormValue = watch(formField);

  const isSelected = selectedFormValue === formValue;

  const handleClick = () => {
    if (disabled) {
      return;
    }

    setValue(formField, formValue);
  };

  return (
    <Stack
      className={cn(
        'p-6 rounded-2xl',
        disabled ? '' : 'hover:bg-neutral-black-4 hover:cursor-pointer',
        isSelected ? 'border-2 border-primary-100' : 'border m-[1px] border-neutral-black-12', // Using m-[1px] to maintain the same spacing in the box due to different border's size
        className,
      )}
      spacing={2}
      onClick={handleClick}
    >
      {title && <span className='typography-body1Semibold'>{title}</span>}
      {description && <span className='typography-body1 text-text-secondary'>{description}</span>}
    </Stack>
  );
};

type FormCardFieldMultipleChoiceProps = { formValue: string } & FormCardProps;

export const FormCardFieldMultipleChoice = ({
  title,
  description,
  formField,
  formValue,
  className,
  disabled,
}: FormCardFieldMultipleChoiceProps) => {
  const { setValue, watch } = useOnboardingFormContext();
  const selectedFormValue = watch(formField) ?? [];

  const isSelected = selectedFormValue.includes(formValue);

  const handleClick = () => {
    if (disabled) {
      return;
    }

    if (!Array.isArray(selectedFormValue)) {
      return;
    }

    const newValues = isSelected
      ? selectedFormValue?.filter((value) => value !== formValue)
      : [...selectedFormValue, formValue];

    setValue(formField, newValues);
  };

  return (
    <Stack
      className={cn(
        'p-6 rounded-2xl',
        disabled ? '' : 'hover:bg-neutral-black-4 hover:cursor-pointer',
        isSelected ? 'border-2 border-primary-100' : 'border m-[1px] border-neutral-black-12', // Using m-[1px] to maintain the same spacing in the box due to different border's size
        className,
      )}
      spacing={2}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={handleClick}
        checkedBoxClasses='text-primary-100'
        disabled={disabled}
      >
        <Stack spacing={2}>
          {title && <span className='typography-body1Semibold'>{title}</span>}
          {description && <span className='typography-body1 text-text-secondary'>{description}</span>}
        </Stack>
      </Checkbox>
    </Stack>
  );
};

type FormCardTextFieldProps = {
  placeholder?: string;
} & FormCardProps;

export const FormCardTextField = ({ formField, placeholder, className, disabled }: FormCardTextFieldProps) => {
  const { setValue, watch } = useOnboardingFormContext();
  const currentValue = (watch(formField) as string) ?? '';

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (disabled) {
      return;
    }
    setValue(formField, e.target.value);
  };

  return (
    <textarea
      className={cn(
        'flex min-h-[300px] w-full rounded-xl border border-neutral-black-12 px-4 py-4 placeholder:text-text-disabled resize-none',
        'focus:border-text-primary focus:outline-none',
        'disabled:cursor-not-allowed disabled:border-text-disabled',
        className,
      )}
      value={currentValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
};
